import styled from 'styled-components';

import { textStyle } from '../../../styles/mixins/_commonPageContentStyles';

// The role chip of the roles table; the size and radius come from `.table-colors .color`.
export const RoleSwatch = styled.span<{ $color: string }>`
  background-color: ${({ $color }) => $color};
`;

// The Updated frame (13627:142411, node 13627:142515) draws this gradient with raw values and binds no variable to it.
// ai-aesthetic-ok: AP-AIU-02 — the gradient is the Figma chip, not a decoration
export const SequentialSwatch = styled.span`
  /* prettier-ignore */
  background: linear-gradient(180deg, #ccb98a 0.01%, #ccb98a 26%, #a7ae7b 28%, #a7ae7b 41%, #a7ae7b 54.49%, #608f7b 54.5%, #45787a 78.99%, #2d4d62 79%); /* tokens-allow: no Figma variable on the frame, see REGISTER DI-032 */
`;

// The two Tokens usage examples (13627:142564) sit side by side, 24px apart.
export const TokenExamples = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.5rem;

  img {
    display: block;
    width: 100%;
    height: auto;
  }
`;

// The Font Colors rules (13780:157131) are H4 lines 4px apart; `&&&` outranks the page's `p` rule.
export const FontColorRules = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-bottom: 1rem;

  &&& p {
    ${textStyle('h4')}
    margin: 0;
  }
`;

export const BrandGroup = styled.div`
  margin-bottom: 2.5rem;
`;

export const GroupTitle = styled.p`
  margin: 0 0 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.neutralTextWeak};

  &&& {
    line-height: 1.142857142857143;
  }
`;

export const BrandRow = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 1rem;
`;

export const BrandSwatchItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
`;

export const BrandChip = styled.div<{ $color: string; $bordered: boolean }>`
  box-sizing: border-box;
  height: 7.5rem;
  background-color: ${({ $color }) => $color};
  border: ${({ $bordered, theme }) => ($bordered ? `1px solid ${theme.neutralBorder}` : 'none')};
`;

// The frame sets the swatch texts in auto line height, Public Sans' 1.175 (16.45px at 14px, 12.92px at 11px).
const AUTO_LINE_HEIGHT = 1.175;

export const BrandName = styled.p`
  margin: 0;
  font-size: 0.875rem;
  font-weight: 600;

  /* The page mixin's .page-content p colour and line height (specificity 0,2,1) beat a single class. */
  &&& {
    color: ${({ theme }) => theme.neutralText};
    line-height: ${AUTO_LINE_HEIGHT};
  }
`;

export const BrandSpecs = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  font-size: 0.6875rem;
  line-height: ${AUTO_LINE_HEIGHT};
  color: ${({ theme }) => theme.neutralTextWeak};
  white-space: nowrap;
`;

export const RampRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
`;

export const RampInfo = styled.div`
  flex: 0 0 6.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`;

export const RampName = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  font-weight: 600;

  &&& {
    color: ${({ theme }) => theme.neutralText};
    line-height: ${AUTO_LINE_HEIGHT};
  }
`;

export const RampSummary = styled.p`
  margin: 0;
  font-size: 0.625rem;
  color: ${({ theme }) => theme.neutralTextWeak};

  &&& {
    line-height: ${AUTO_LINE_HEIGHT};
  }
`;

// One column per step of the longest ramp (Neutral: 0 and 50-900). A ramp without a step 0 starts
// at the second column, so every step sits under the same step of the Neutral ramp.
export const RampSteps = styled.div<{ $skipFirstColumn: boolean }>`
  display: grid;
  flex: 1;
  grid-template-columns: repeat(11, minmax(0, 1fr));
  gap: 0.25rem;
  min-width: 0;

  ${({ $skipFirstColumn }) =>
    $skipFirstColumn &&
    `
    > :first-child {
      grid-column-start: 2;
    }
  `}
`;

export const RampStepItem = styled.div<{ $color: string; $label: string; $bordered: boolean }>`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
  height: 6.5rem;
  padding: 0.5rem;
  overflow: hidden;
  line-height: ${AUTO_LINE_HEIGHT};
  background-color: ${({ $color }) => $color};
  color: ${({ $label }) => $label};
  border: ${({ $bordered, theme }) => ($bordered ? `1px solid ${theme.neutralBorder}` : 'none')};

  .step {
    font-size: 0.6875rem;
    font-weight: 600;
  }

  .hex {
    font-size: 0.625rem;
    opacity: 0.9;
  }

  .contrast {
    font-size: 0.5rem;
    opacity: 0.75;
  }
`;
